import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FiRepeat, FiDownload, FiUpload, FiShuffle, FiGrid } from 'react-icons/fi';
import type { IconType } from 'react-icons';
import type { AppDispatch, RootState } from '../../store';
import { setSelectedProject } from '../../store/project/projectSlice';
import { usePresence } from '../../hooks/usePresence';
import { useProjectTransfer } from './useProjectTransfer';
import MappingSpecDialog from '../mappingSpec/MappingSpecDialog';

/**
 * 시스템 버튼 줄 맨 왼쪽의 「프로젝트」 — 누르면 아래로 변경·내보내기·불러오기·테라스트림에서 가져오기가 펼쳐진다.
 * 예전에는 사용자 메뉴 안에 있었다(2026-10-04). 오른쪽으로 펼쳐 다른 버튼을 미는 방식은 일반적이지 않아 아래로 바꿨다.
 *
 * 메뉴는 천천히 나타나고 사라진다(usePresence) — 툭 붙었다 떨어지면 급작스럽다.
 * 바깥을 누르거나 Esc 면 닫힌다. 캔버스(React Flow)는 mousedown 을 삼키므로 캡처 단계의 pointerdown 으로 듣는다.
 */
const ProjectMenu: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const projectId = useSelector((s: RootState) => s.project.selectedProjectId);
  const hasProject = !!projectId;
  const [specOpen, setSpecOpen] = useState(false);
  const transfer = useProjectTransfer();
  const [open, setOpen] = useState(false);
  const menu = usePresence(open, 160);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDown, true); document.removeEventListener('keydown', onKey); };
  }, [open]);

  /** 하위 동작 — 누르면 닫고 실행한다 */
  // 아이콘은 사용자 메뉴(AppHeader)와 같은 react-icons 선 아이콘 — 이모지를 섞으면 두 메뉴의 모양이 달라 보인다
  const item = (label: string, Icon: IconType, title: string, fn: () => void, point: string, opts: { disabled?: boolean; divider?: boolean } = {}) => (
    <button type="button" title={title} data-point={point} disabled={opts.disabled}
      className={classNames('w-full flex items-center gap-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent',
        opts.divider && 'border-t border-gray-200')}
      onClick={() => { setOpen(false); fn(); }}>
      {/* @ts-ignore react-icons IconType 과 React 18 타입 불일치 회피 */}
      <Icon className="shrink-0" /> {label}
    </button>
  );

  return (
    <div ref={ref} className="relative shrink-0 mr-4">
      <button type="button"
        className={classNames('text-sm px-3 py-1.5 rounded-md border flex items-center gap-1 whitespace-nowrap',
          open ? 'bg-blue-50 border-blue-400 text-blue-700' : 'text-gray-700 hover:bg-gray-100 border-gray-300')}
        onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="menu" data-point="header:project" title={t('layout:projectMenu.title')}>
        <span>📂</span> {t('layout:projectMenu.label')}
        <span className={classNames('text-xs transition-transform duration-200', open && 'rotate-180')}>▾</span>
      </button>
      {menu.mounted && (
        <div role="menu"
          className={classNames('absolute left-0 top-[calc(100%+8px)] min-w-[220px] text-sm bg-white border border-gray-200 rounded-lg shadow-lg z-[1000] overflow-hidden origin-top-left transition-all duration-150 ease-out',
            menu.shown ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-1 scale-95 pointer-events-none')}>
          {item(t('layout:projectMenu.change'), FiRepeat, t('layout:projectMenu.changeTitle'), () => dispatch(setSelectedProject(null)), 'header:project.change')}
          {item(t('layout:transfer.export'), FiDownload, t('layout:transfer.exportTitle'), transfer.exportProject, 'header:project.export', { disabled: !hasProject, divider: true })}
          {item(t('layout:transfer.import'), FiUpload, t('layout:transfer.importTitle'), transfer.importProject, 'header:project.import')}
          {item(t('layout:migrate.menu'), FiShuffle, t('layout:migrate.menuTitle'), transfer.importTeraStream, 'header:project.terastream', { divider: true })}
          {item(t('mapping:menu'), FiGrid, t('mapping:menuTitle'), () => setSpecOpen(true), 'header:project.mappingSpec', { disabled: !hasProject, divider: true })}
        </div>
      )}
      {/* 불러오기·이관 창과 진행 표시는 문서 맨 위층으로 — 버튼 줄 안에 두면 그 층에 갇혀 캔버스 도구 막대가 창 위로 올라왔다 */}
      {createPortal(transfer.element, document.body)}
      {specOpen && projectId && createPortal(<MappingSpecDialog projectId={projectId} onClose={() => setSpecOpen(false)} />, document.body)}
    </div>
  );
};

export default ProjectMenu;
